import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { setupColumnAutoFit, autoFitAllColumns } from "../utils/auiGridAutoFit";
import { setupFillHandle } from "../utils/auiGridFillHandle";

/**
 * RichInvoiceGrid — 딥러닝 탭의 AUIGrid 기능을 컴포넌트화
 *
 * 기능
 *   - 셀 편집, 다중 셀 선택, Fill Handle, Autofit
 *   - 헤더 우클릭 메뉴: 정렬 / Trim / UPPERCASE / lowercase / 숫자만 / 문자만 / 특수문자 제거 / 찾기·바꾸기 / 열 삭제 / 빈 행 삭제
 *   - 셀 우클릭 메뉴: 행 삽입·추가·삭제, 셀 내용 지우기, 위로/왼쪽으로 병합
 *   - 단축키: Delete(셀 비우기), Ctrl+'+'(행 추가), Ctrl+'-'(행 삭제), F2(편집)
 *   - 숫자형 컬럼 SUM footer
 *
 * Props
 *   gridId      (string, 필수) AUIGrid 컨테이너 DOM id (페이지 내 유일)
 *   columns     [{ dataField, headerText, width, editable?, numeric?, style?, headerStyle? }]
 *   data        rows 배열
 *   onChange    (newRows) => void  — 데이터 변경 시 호출 (편집/추가/삭제/변환 모두)
 *   height      높이(px) — 기본 300
 *   footerSum   boolean — 숫자형 컬럼 SUM 푸터 노출 (기본 true)
 *   rowNumHeaderText  기본 'No'
 *   className   외부 래퍼 class
 */
const NUMERIC_RE =
  /amount|price|qty|quantity|weight|cbm|count|total|vat|tax|cost|fee|charge|수량|단가|금액|중량|부피|합계/i;

export default function RichInvoiceGrid({
  gridId,
  columns,
  data = [],
  onChange,
  onCellClick,
  height = 300,
  footerSum = true,
  rowNumHeaderText = "No",
  className = "",
  showRowNumColumn = true,
  showStateColumn = true,
  autoFit = true,
  rowNumWidth,
  enableMovingColumn = true,
}) {
  // onCellClick 의 latest ref 유지 — useEffect 가 grid 를 다시 만들지 않도록
  const onCellClickRef = useRef(onCellClick);
  useEffect(() => {
    onCellClickRef.current = onCellClick;
  }, [onCellClick]);
  const gridRef = useRef(null);
  const dataRef = useRef(data);
  // 외부 data 변경 → reference 비교로 echo (syncFromGrid → 외부 setState → 동일 ref 재전달) skip.
  // length 만 비교하면 같은 길이의 데이터 교체 (예: sessionStorage 재import) 가 무시되는 버그 방지.
  useEffect(() => {
    const prev = dataRef.current;
    dataRef.current = data;
    if (!gridRef.current || !window.AUIGrid) return;
    if (data === prev) return;
    try {
      window.AUIGrid.setGridData(gridRef.current, data || []);
    } catch (e) {
      /* ignore */
    }
  }, [data]);

  // 헤더 컨텍스트 메뉴 상태
  const [headerMenu, setHeaderMenu] = useState(null); // { x, y, field, headerText }
  const [findReplace, setFindReplace] = useState(null); // { field, headerText, find, replace }

  const columnsKey = useMemo(
    () => columns.map((c) => c.dataField).join("|"),
    [columns],
  );

  // ── 유틸: 변경된 rows를 외부로 전파 ────────────────────────────
  const propagate = useCallback(
    (newRows) => {
      dataRef.current = newRows;
      if (onChange) onChange(newRows);
    },
    [onChange],
  );

  // 현재 그리드의 최신 rows를 읽어 외부 state로 동기화
  const syncFromGrid = useCallback(() => {
    if (!gridRef.current || !window.AUIGrid) return;
    try {
      const rows = window.AUIGrid.getGridData(gridRef.current) || [];
      // AUIGrid 내부 플래그(_$uid 등)는 skip하되, 외부에서 부여한 메타 (밑줄 prefix, 예: _aiSuggestion / _provenance) 는 보존
      const clean = rows.map((r) => {
        const o = {};
        columns.forEach((c) => {
          o[c.dataField] = r[c.dataField] ?? "";
        });
        Object.keys(r).forEach((k) => {
          if (k.startsWith("_") && !k.startsWith("_$")) o[k] = r[k];
        });
        return o;
      });
      propagate(clean);
    } catch (e) {
      /* ignore */
    }
  }, [columns, propagate]);

  // ── 그리드 생성 ────────────────────────────────────────────────
  useEffect(() => {
    if (!window.AUIGrid) return;

    // 이전 인스턴스 제거
    if (gridRef.current) {
      try {
        window.AUIGrid.destroy(gridRef.current);
      } catch (e) {}
      gridRef.current = null;
    }

    let nativeClickCleanup = null;
    const t = setTimeout(() => {
      const el = document.getElementById(gridId);
      if (!el) return;

      const columnLayout = columns.map((c) => {
        const col = {
          dataField: c.dataField,
          headerText: c.headerText || c.dataField,
          width: c.width || 100,
          editable: c.editable !== false,
          editRenderer: c.editRenderer || { type: "InputEditRenderer" },
          style: c.style || "left-align",
          headerStyle: c.headerStyle || "center-align",
        };
        if (c.styleFunction) col.styleFunction = c.styleFunction;
        if (c.labelFunction) col.labelFunction = c.labelFunction;
        if (c.renderer) col.renderer = c.renderer;
        if (c.dataType) col.dataType = c.dataType;
        return col;
      });

      const gridProps = {
        headerHeight: 32,
        rowHeight: 28,
        hScrollPolicy: "auto",
        enableFilter: false,
        enableSorting: false,
        enableColumnResize: true,
        enableMovingColumn: enableMovingColumn,
        showRowNumColumn,
        rowNumHeaderText,
        ...(typeof rowNumWidth === "number" ? { rowNumWidth } : {}),
        editable: true,
        selectionMode: "multipleCells",
        showStateColumn,
        showFooter: footerSum,
        footerPosition: "bottom",
        footerHeight: 24,
        useContextMenu: true,
        // 기본 내장 셀 컨텍스트 메뉴 (헤더는 별도 React 오버레이로 처리)
        contextMenuItems: [
          { label: "행 삽입 (위)", callback: "insertRow" },
          { label: "행 추가 (아래)", callback: "addRow" },
          { label: "-" },
          { label: "선택 행 삭제", callback: "deleteSelectedRows" },
          { label: "-" },
          { label: "셀 내용 지우기", callback: "clearCell" },
          { label: "위로 병합", callback: "mergeUp" },
          { label: "왼쪽으로 병합", callback: "mergeLeft" },
        ],
      };

      const grid = window.AUIGrid.create(`#${gridId}`, columnLayout, gridProps);
      gridRef.current = grid;

      window.AUIGrid.setGridData(grid, dataRef.current || []);

      // flex 타이밍 이슈 방지: 강제 reflow + autofit
      requestAnimationFrame(() => {
        try {
          window.AUIGrid.resize(grid);
          if (autoFit) autoFitAllColumns(grid);
        } catch (e) {}
      });

      // 자동 폭 맞춤(헤더 경계 더블클릭) — autoFit off 시도 헤더 더블클릭은 활성
      setupColumnAutoFit(grid, `#${gridId}`);

      // Fill Handle (엑셀식)
      setupFillHandle(grid, `#${gridId}`, {
        onFill: () => {
          syncFromGrid();
        },
      });

      // 셀 편집 완료 → 외부 state 반영
      window.AUIGrid.bind(grid, "cellEditEnd", () => {
        syncFromGrid();
      });

      // 셀 클릭 — 외부 핸들러. editable cell + multipleCells 모드에서 cellClick 미발화 케이스
      // 대비: cellClick / cellSelected / rowSelected 모두 bind. 마지막 발화 cell 만 1회 처리 (debounce).
      let _lastFireKey = "";
      const fireOnce = (event) => {
        if (!event || !onCellClickRef.current) return;
        const key = `${event.rowIndex}|${event.dataField || ""}`;
        if (key === _lastFireKey) return;
        _lastFireKey = key;
        // 다음 click 까지 reset 위한 micro-timer
        setTimeout(() => {
          _lastFireKey = "";
        }, 200);
        try {
          onCellClickRef.current(event);
        } catch (e) {
          /* ignore */
        }
      };
      // 일반 클릭(비편집 셀) — event.item 포함.
      try {
        window.AUIGrid.bind(grid, "cellClick", fireOnce);
      } catch (e) {}
      // ⭐⭐ editable 셀은 단일 클릭 시 곧바로 '편집 모드'로 진입해 cellClick/selectionChange 가
      //    발화하지 않는다 → cellEditBegin 으로 클릭을 포착 (rowIndex/item 포함). 편집은 막지 않음.
      try {
        window.AUIGrid.bind(grid, "cellEditBegin", fireOnce);
      } catch (e) {}
      // ⭐ editable + multipleCells 셀은 cellClick 이 미발화하는 케이스가 있어
      //    selectionChange 로 보강. ('cellSelected' 는 AUIGrid 에 없는 no-op 이벤트였음 → 제거)
      //    getSelectedIndex 로 선택 행 index 를 직접 조회해 {rowIndex,item} 합성.
      const fireFromSelection = () => {
        try {
          const idxs = window.AUIGrid.getSelectedIndex(`#${gridId}`);
          const rowIndex = Array.isArray(idxs) ? idxs[0] : idxs;
          if (rowIndex == null || rowIndex < 0) return;
          fireOnce({ rowIndex, item: (dataRef.current || [])[rowIndex] });
        } catch (e) {}
      };
      ["selectionChange", "selectionDown", "selectionUp"].forEach((ev) => {
        try {
          window.AUIGrid.bind(grid, ev, fireFromSelection);
        } catch (e) {}
      });

      // 🛟 네이티브 DOM 클릭 폴백 — AUIGrid 이벤트(cellClick/selectionChange/cellEditBegin)가
      //    editable+multipleCells 조합에서 전혀 안 떠도, 컨테이너 클릭 후 getSelectedIndex 로
      //    선택 행을 직접 읽어 onCellClick 발화. (가장 확실한 최후 보루)
      const onNativeClick = () => {
        setTimeout(() => {
          try {
            const idxs = window.AUIGrid.getSelectedIndex(`#${gridId}`);
            const rowIndex = Array.isArray(idxs) ? idxs[0] : idxs;
            if (rowIndex == null || rowIndex < 0) return;
            fireOnce({
              rowIndex,
              dataField: "_native",
              item: (dataRef.current || [])[rowIndex],
            });
          } catch (e) {}
        }, 0);
      };
      el.addEventListener("click", onNativeClick);
      nativeClickCleanup = () => {
        try {
          el.removeEventListener("click", onNativeClick);
        } catch (e) {}
      };

      // 행 추가/삭제 이벤트도 동기화
      ["addRow", "removeRow", "rowDataChanged", "setData"].forEach((ev) => {
        try {
          window.AUIGrid.bind(grid, ev, () => {
            syncFromGrid();
          });
        } catch (e) {}
      });

      // 헤더 우클릭 → React 오버레이 메뉴
      window.AUIGrid.bind(grid, "contextMenu", function (event) {
        if (event.target === "header") {
          // 기본 AUIGrid 컨텍스트 메뉴 차단
          setHeaderMenu({
            x: event.pageX,
            y: event.pageY,
            field: event.dataField,
            headerText: event.headerText || event.dataField,
          });
          return false;
        }
      });

      // 키보드 단축키
      window.AUIGrid.bind(grid, "keyDown", function (event) {
        // Delete → 선택 셀 비우기
        if (event.keyCode === 46) {
          const sel = window.AUIGrid.getSelectedItems(grid);
          if (sel && sel.length) {
            sel.forEach((s) => {
              window.AUIGrid.updateRow(grid, { [s.dataField]: "" }, s.rowIndex);
            });
            syncFromGrid();
            return false;
          }
        }
        // Ctrl + '+' → 행 추가
        if (
          (event.ctrlKey || event.metaKey) &&
          (event.keyCode === 187 ||
            event.keyCode === 107 ||
            event.key === "+" ||
            event.key === "=")
        ) {
          event.preventDefault?.();
          window.AUIGrid.addRow(grid, {}, "selectionDown");
          setTimeout(syncFromGrid, 50);
          return false;
        }
        // Ctrl + '-' → 선택 행 삭제
        if (
          (event.ctrlKey || event.metaKey) &&
          (event.keyCode === 189 ||
            event.keyCode === 109 ||
            event.key === "-" ||
            event.key === "_")
        ) {
          event.preventDefault?.();
          const sel = window.AUIGrid.getSelectedItems(grid);
          if (sel && sel.length) {
            const indices = Array.from(
              new Set(sel.map((s) => s.rowIndex)),
            ).sort((a, b) => b - a);
            indices.forEach((i) => window.AUIGrid.removeRow(grid, i));
            setTimeout(syncFromGrid, 50);
          }
          return false;
        }
        // F2 → 편집 모드
        if (event.keyCode === 113) {
          window.AUIGrid.openInputer(grid);
          return false;
        }
      });

      // 푸터: 숫자형 컬럼에 SUM 설정
      if (footerSum) {
        const footerLayout = columns
          .filter((c) => c.numeric === true || NUMERIC_RE.test(c.dataField))
          .map((c) => ({
            dataField: c.dataField,
            positionField: c.dataField,
            operation: "SUM",
            formatString: "#,##0.##",
            style: "text-align: right; font-weight: bold; color: #333;",
          }));
        if (footerLayout.length > 0) {
          try {
            window.AUIGrid.setFooter(grid, footerLayout);
          } catch (e) {}
        }
      }
    }, 60);

    return () => {
      clearTimeout(t);
      if (nativeClickCleanup) nativeClickCleanup();
      if (gridRef.current) {
        try {
          window.AUIGrid.destroy(gridRef.current);
        } catch (e) {}
        gridRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridId, columnsKey]);

  // 컨테이너(좌측 패널 등) 크기 변경 시 AUIGrid 리플로우 —
  // 가변(width:"100%") 컬럼이 패널 너비에 맞게 늘어나도록 (패널 드래그 리사이즈 대응).
  useEffect(() => {
    const el = document.getElementById(gridId);
    if (!el || typeof ResizeObserver === "undefined") return;
    let raf = null;
    const ro = new ResizeObserver(() => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try {
          if (gridRef.current && window.AUIGrid?.isCreated?.(gridRef.current)) {
            window.AUIGrid.resize(gridRef.current);
          }
        } catch (e) {}
      });
    });
    ro.observe(el);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [gridId]);

  // 외부 data 변경 = 위 통합 useEffect 가 처리 (length 만 비교하던 옛 로직 제거)

  // 외부 클릭 시 헤더 메뉴 닫기
  useEffect(() => {
    if (!headerMenu) return;
    const onDown = (e) => {
      if (!e.target.closest("[data-rig-header-menu]")) setHeaderMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [headerMenu]);

  // ── 헤더 메뉴 동작 ────────────────────────────────────────────
  const applyTransform = useCallback(
    (field, fn) => {
      if (!gridRef.current || !window.AUIGrid) return;
      const rows = window.AUIGrid.getGridData(gridRef.current) || [];
      const next = rows.map((r) => ({
        ...r,
        [field]: fn(String(r[field] ?? "")),
      }));
      window.AUIGrid.setGridData(gridRef.current, next);
      syncFromGrid();
    },
    [syncFromGrid],
  );

  const sortByColumn = useCallback(
    (field, dir) => {
      if (!gridRef.current || !window.AUIGrid) return;
      const rows = [...(window.AUIGrid.getGridData(gridRef.current) || [])];
      const mul = dir === "asc" ? 1 : -1;
      rows.sort((a, b) => {
        const av = a[field] ?? "";
        const bv = b[field] ?? "";
        const an = parseFloat(String(av).replace(/,/g, ""));
        const bn = parseFloat(String(bv).replace(/,/g, ""));
        if (!isNaN(an) && !isNaN(bn)) return (an - bn) * mul;
        return String(av).localeCompare(String(bv), "ko") * mul;
      });
      window.AUIGrid.setGridData(gridRef.current, rows);
      syncFromGrid();
    },
    [syncFromGrid],
  );

  const deleteEmptyRows = useCallback(() => {
    if (!gridRef.current || !window.AUIGrid) return;
    const rows = (window.AUIGrid.getGridData(gridRef.current) || []).filter(
      (r) => columns.some((c) => String(r[c.dataField] ?? "").trim() !== ""),
    );
    window.AUIGrid.setGridData(gridRef.current, rows);
    syncFromGrid();
  }, [columns, syncFromGrid]);

  const clearColumn = useCallback(
    (field) => {
      applyTransform(field, () => "");
    },
    [applyTransform],
  );

  // ── 렌더 ─────────────────────────────────────────────────────
  // height === '100%' or 'auto'이면 컨테이너 전체 채움
  const useFill = height === "100%" || height === "auto" || height === "fill";
  return (
    <div
      className={`relative flex flex-col ${className}`}
      style={
        useFill ? { height: "100%", minHeight: 200 } : { minHeight: height }
      }
    >
      <div
        id={gridId}
        style={
          useFill
            ? { width: "100%", flex: "1 1 auto", minHeight: 0 }
            : { width: "100%", height }
        }
      />

      {/* 헤더 우클릭 메뉴 */}
      {headerMenu && (
        <div
          data-rig-header-menu
          className="fixed bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-[60] min-w-[220px] text-[12px]"
          style={{
            left: Math.min(headerMenu.x, window.innerWidth - 240),
            top: Math.min(headerMenu.y, window.innerHeight - 400),
          }}
        >
          <div className="px-3 py-1.5 text-[11px] text-gray-500 border-b border-gray-100">
            열:{" "}
            <span className="font-semibold text-gray-800">
              {headerMenu.headerText}
            </span>
          </div>
          <MenuItem
            label="오름차순 정렬"
            onClick={() => {
              sortByColumn(headerMenu.field, "asc");
              setHeaderMenu(null);
            }}
          />
          <MenuItem
            label="내림차순 정렬"
            onClick={() => {
              sortByColumn(headerMenu.field, "desc");
              setHeaderMenu(null);
            }}
          />
          <div className="my-1 border-t border-gray-100" />
          <MenuItem
            label="Trim (앞뒤 공백 제거)"
            hint="Alt+T"
            onClick={() => {
              applyTransform(headerMenu.field, (v) => v.trim());
              setHeaderMenu(null);
            }}
          />
          <MenuItem
            label="UPPERCASE"
            hint="Alt+U"
            onClick={() => {
              applyTransform(headerMenu.field, (v) => v.toUpperCase());
              setHeaderMenu(null);
            }}
          />
          <MenuItem
            label="lowercase"
            onClick={() => {
              applyTransform(headerMenu.field, (v) => v.toLowerCase());
              setHeaderMenu(null);
            }}
          />
          <MenuItem
            label="숫자만 남기기"
            onClick={() => {
              applyTransform(headerMenu.field, (v) =>
                v.replace(/[^0-9.\-]/g, ""),
              );
              setHeaderMenu(null);
            }}
          />
          <MenuItem
            label="문자만 남기기"
            onClick={() => {
              applyTransform(headerMenu.field, (v) => v.replace(/[0-9]/g, ""));
              setHeaderMenu(null);
            }}
          />
          <MenuItem
            label="특수문자 제거"
            onClick={() => {
              applyTransform(headerMenu.field, (v) =>
                v.replace(/[^\p{L}\p{N}\s]/gu, ""),
              );
              setHeaderMenu(null);
            }}
          />
          <div className="my-1 border-t border-gray-100" />
          <MenuItem
            label="찾기/바꾸기..."
            hint="Ctrl+H"
            onClick={() => {
              setFindReplace({
                field: headerMenu.field,
                headerText: headerMenu.headerText,
                find: "",
                replace: "",
              });
              setHeaderMenu(null);
            }}
          />
          <MenuItem
            label="열 내용 비우기"
            onClick={() => {
              clearColumn(headerMenu.field);
              setHeaderMenu(null);
            }}
          />
          <div className="my-1 border-t border-gray-100" />
          <MenuItem
            label="빈 행 삭제"
            onClick={() => {
              deleteEmptyRows();
              setHeaderMenu(null);
            }}
          />
        </div>
      )}

      {/* 찾기/바꾸기 모달 */}
      {findReplace && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-[70]"
          onClick={() => setFindReplace(null)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-[360px] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-sm font-semibold text-gray-800 mb-3">
              찾기/바꾸기 —{" "}
              <span className="text-blue-700">{findReplace.headerText}</span>
            </div>
            <div className="space-y-2">
              <div>
                <label className="text-[11px] text-gray-500">찾을 값</label>
                <input
                  autoFocus
                  value={findReplace.find}
                  onChange={(e) =>
                    setFindReplace((fr) => ({ ...fr, find: e.target.value }))
                  }
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-500">바꿀 값</label>
                <input
                  value={findReplace.replace}
                  onChange={(e) =>
                    setFindReplace((fr) => ({ ...fr, replace: e.target.value }))
                  }
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setFindReplace(null)}
                className="text-xs border border-gray-300 rounded px-3 py-1.5 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={() => {
                  if (findReplace.find === "") {
                    setFindReplace(null);
                    return;
                  }
                  const f = findReplace.find;
                  applyTransform(findReplace.field, (v) =>
                    v.split(f).join(findReplace.replace),
                  );
                  setFindReplace(null);
                }}
                className="text-xs bg-blue-600 text-white rounded px-3 py-1.5 hover:bg-blue-700"
              >
                전체 바꾸기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ label, hint, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center justify-between text-gray-700"
    >
      <span>{label}</span>
      {hint && <span className="text-[10px] text-gray-400 ml-3">{hint}</span>}
    </button>
  );
}
